import { PropsWithChildren, useEffect } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner';
import { Button as WPLButton, Icon } from '@atlassiansox/wpl-utils';
import { TRACKING_EVENT_NAMES } from '../../utils/constants';
import {
  buttonWrapper,
  containerStyle,
  descriptionStyle,
  dividerStyle,
  footerContainerStyle,
  headerContainerStyle,
  headerStyle,
  spinnerContainerStyle,
  taxSubtextStyle,
  totalValueStyle
} from './entitlements-popup.styles';
import { ErrorState } from './error-state';

export interface EntitlementsPopupProps {
  addPaymentDetailsURL: string;
  analyticsAttributes: object;
  hasError: boolean;
  isLoading: boolean;
  formattedTotalPrice: string;
}

const EntitlementsPopup = ({
  addPaymentDetailsURL,
  analyticsAttributes,
  children,
  formattedTotalPrice,
  hasError,
  isLoading
}: PropsWithChildren<EntitlementsPopupProps>) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const handleAddPaymentDetails = () => {
    createAnalyticsEvent({
      eventName: TRACKING_EVENT_NAMES.wacCartAddPaymentDetailsButtonClicked,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'wacCartAddPaymentDetails',
      attributes: analyticsAttributes
    }).fire();
  };

  useEffect(() => {
    createAnalyticsEvent({
      eventName: TRACKING_EVENT_NAMES.wacCartPopoverViewed,
      action: 'viewed',
      actionSubjectId: 'wacCartPopover',
      attributes: analyticsAttributes
    }).fire();
  }, []);

  if (hasError) {
    return (
      <div css={containerStyle}>
        <ErrorState analyticsAttributes={analyticsAttributes} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div css={spinnerContainerStyle}>
        <Spinner size="xlarge" />
      </div>
    );
  }

  return (
    <div css={containerStyle}>
      <div css={headerContainerStyle}>
        <Icon name="exclamation" size="xs" />
        <h3 css={headerStyle}>Action required</h3>
      </div>
      <div css={descriptionStyle}>
        To avoid product deactivation, add your payment details now. Your
        payment details will be stored in your account for the following plans
        and any future purchases you make.
      </div>
      <div css={dividerStyle} />

      <div>{children}</div>

      <div css={dividerStyle} />
      <div css={footerContainerStyle}>
        <div>Monthly total</div>
        <div css={totalValueStyle}>{formattedTotalPrice}</div>
      </div>
      <div css={taxSubtextStyle}>(excluding tax)</div>
      <div css={buttonWrapper}>
        <WPLButton
          text="Add payment details"
          color="green"
          onClick={handleAddPaymentDetails}
          url={addPaymentDetailsURL}
        />
      </div>
    </div>
  );
};

export { EntitlementsPopup };
